import { useEffect, useState } from "react";
import { BarChart3, Eye, MousePointerClick, MapPin } from "lucide-react";
import { api } from "@/lib/api";

const RANGES = [
  { v: 7, label: "7 j" },
  { v: 30, label: "30 j" },
  { v: 90, label: "90 j" },
  { v: 365, label: "1 an" },
];

export default function AdminAnalytics() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/analytics/overview", { params: { days } }).then((r) => setData(r.data)).catch(() => {});
  }, [days]);

  if (!data) return <div className="text-ink-300">Chargement…</div>;

  const cards = [
    { label: `Pages vues (${days}j)`, value: data.pageviews_period, Icon: Eye },
    { label: "Pages vues total", value: data.pageviews_total, Icon: Eye },
    { label: `Clics (${days}j)`, value: data.events_period, Icon: MousePointerClick },
    { label: "Clics total", value: data.events_total, Icon: MousePointerClick },
  ];

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-heading text-3xl lg:text-4xl font-black uppercase tracking-tight">Statistiques</div>
          <div className="h-1 w-16 bg-brand mt-2" />
        </div>
        <div className="flex items-center gap-2">
          {RANGES.map((r) => (
            <button key={r.v} onClick={() => setDays(r.v)} className={`px-3 py-2 text-xs font-bold uppercase tracking-wider border ${days === r.v ? "bg-brand text-white border-brand" : "border-ink-700 text-ink-300 hover:border-brand"}`}>{r.label}</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
        {cards.map((c) => (
          <div key={c.label} className="p-5 border border-ink-700 bg-ink-900">
            <c.Icon className="text-ink-400" size={20} />
            <div className="font-heading text-3xl font-black mt-3">{(c.value ?? 0).toLocaleString("fr-FR")}</div>
            <div className="text-ink-300 text-xs uppercase tracking-wider mt-1">{c.label}</div>
          </div>
        ))}
      </div>

      <Section title="Vues par page" icon={Eye} empty="Pas encore de visites">
        <Bars rows={(data.per_path || []).slice(0, 20).map((r) => ({ label: r.path, count: r.count }))} />
      </Section>

      <Section title="Vues par jour" icon={BarChart3} empty="Pas encore de visites">
        <Bars rows={(data.per_day || []).slice(-30).map((r) => ({ label: r.day, count: r.count }))} />
      </Section>

      <Section title="Évènements par type" icon={MousePointerClick} empty="Aucun clic tracé">
        <Bars rows={(data.by_kind || []).map((r) => ({ label: r.kind, count: r.count }))} />
      </Section>

      <Section title="Clics par département" icon={MapPin} empty="Aucun clic département">
        <div className="bg-ink-900 border border-ink-700 overflow-x-auto">
          <table className="w-full text-sm whitespace-nowrap">
            <thead className="bg-ink-800 text-ink-300 uppercase text-xs tracking-wider">
              <tr><th className="text-left px-4 py-2">Code</th><th className="text-left px-4 py-2">Département</th><th className="text-left px-4 py-2">Slug</th><th className="text-right px-4 py-2">Clics</th></tr>
            </thead>
            <tbody className="divide-y divide-ink-700">
              {(data.departments || []).map((d) => (
                <tr key={d.slug}>
                  <td className="px-4 py-2 font-bold text-brand">{d.code || "—"}</td>
                  <td className="px-4 py-2">{d.name || "—"}</td>
                  <td className="px-4 py-2 text-ink-300 text-xs">{d.slug}</td>
                  <td className="px-4 py-2 text-right font-bold">{d.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  );
}

function Section({ title, icon: Icon, children, empty }) {
  return (
    <div className="mt-8">
      <div className="flex items-center gap-2 mb-3">
        <Icon size={18} className="text-brand" />
        <div className="font-heading text-xl font-bold uppercase tracking-tight">{title}</div>
      </div>
      {children}
    </div>
  );
}

function Bars({ rows }) {
  if (!rows?.length) return <div className="text-ink-400 text-sm bg-ink-900 border border-ink-700 p-4">Aucune donnée</div>;
  const max = Math.max(...rows.map((r) => r.count), 1);
  return (
    <div className="bg-ink-900 border border-ink-700 p-4 space-y-2">
      {rows.map((r) => (
        <div key={r.label} className="grid grid-cols-12 items-center gap-2">
          <div className="col-span-5 sm:col-span-4 text-xs text-ink-200 truncate" title={r.label}>{r.label}</div>
          <div className="col-span-5 sm:col-span-7 h-3 bg-ink-800 relative">
            <div className="absolute inset-y-0 left-0 bg-brand" style={{ width: `${(r.count / max) * 100}%` }} />
          </div>
          <div className="col-span-2 sm:col-span-1 text-right font-bold">{r.count}</div>
        </div>
      ))}
    </div>
  );
}
